import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  DialogActions,
  Button,
  useTheme,
  Alert,
  CircularProgress
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../../state/state';
import { closeModal } from '../../../../state/ModalSlice/ModalSlice';
import { persistActiveRole, selectAvailableRoles, selectActiveRole } from '../../../../state/UserStatesV2/userSliceV2';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { RootState } from '../../../../state/state';
import { ServiceProviderRole } from '../../types/UserStateV2';
import { toast } from 'react-toastify';

export const RoleSwitchModal = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector((state) => state.ModalSlice.RoleSwitchModalOpen);
  const userData = useAppSelector((state: RootState) => state.userSliceV2.data);
  const userStatus = useAppSelector((state: RootState) => state.userSliceV2.status);
  const myRoles = useAppSelector(selectAvailableRoles);
  const currentActiveRole = useAppSelector(selectActiveRole);

  // Debug logs
  // React.useEffect(() => {
  //   console.log("UserData:", userData);
  //   console.log("User Status:", userStatus);
  //   console.log("My Roles from selector:", myRoles);
  //   console.log("Active Role from selector:", currentActiveRole);
  // }, [userData, userStatus, myRoles, currentActiveRole]);

  const sortedRoles = React.useMemo(() => {
    if (!myRoles) return [];

    const sorted = [...myRoles].sort((a, b) => {
      // Sort by role type (Administrator first)
      if (a.role === 'Administrator' && b.role !== 'Administrator') return -1;
      if (a.role !== 'Administrator' && b.role === 'Administrator') return 1;

      // Then sort by business name
      return (a.businessName || '').localeCompare(b.businessName || '');
    });

    return sorted;
  }, [myRoles]);





  const handleClose = () => {
    dispatch(closeModal("RoleSwitchModalOpen"));
  };

  const handleRoleSelect = async (role: ServiceProviderRole) => {

    try {
      await dispatch(persistActiveRole(role));
      toast.success("Successfully switched to role");
      handleClose();
    } catch (error) {
      console.error("Error switching role:", error);
    }
  };

  const handleClearRole = async () => {

    try {
      await dispatch(persistActiveRole(null));
      toast.success("Successfully switched to personal account");
      handleClose();
    } catch (error) {
      console.error("Error clearing active role:", error);
    }
  };

  if (userStatus === 'not_loaded') {
    return (
      <Dialog
        open={isOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  const isAdminView = currentActiveRole?.role === 'Administrator';

  return (
    <Dialog
      sx={{
        padding: 0,
        '& .MuiPaper-root': {
          borderRadius: '10px',
        },
      }}
      maxWidth="sm"
      open={isOpen}
      onClose={handleClose}
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '12px',
          backgroundColor: '#FFFFFF'
        },
      }}
    >
      <DialogTitle sx={{
        m: 0,
        p: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
      }}>
        <Box>
          <p className='text-xl font-semibold'>Switch Role</p>
          <p className='text-sm text-gray-500'>
            Select a role to switch context or return to your account
          </p>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2, mt: 1 }}>

        <div className="w-full space-y-3">
          {/* Owner Account Item */}
          <div
            role="button"
            tabIndex={0}
            onClick={handleClearRole}
            onKeyDown={(e) => e.key === 'Enter' && handleClearRole()}
            aria-label="Switch to personal account"
            className={`
              relative p-4 rounded-xl cursor-pointer transition-all duration-200
              ${!currentActiveRole
                ? 'bg-indigo-50/80 border-2 border-indigo-400 shadow-sm'
                : 'bg-white hover:bg-slate-50/50 border border-slate-200'
              }
              hover:shadow-md hover:-translate-y-[1px]
              focus:outline-none focus:ring-2 focus:ring-indigo-500/30
            `}
          >
            <div className="flex justify-between mb-4 items-center">
              <div className="space-y-1">
                <p className={`text-lg font-semibold ${!currentActiveRole ? 'text-indigo-700' : 'text-slate-700'}`}>
                  My Account ({userData?.display_name || 'Loading...'})
                </p>
                <p className={`text-sm ${!currentActiveRole ? 'text-indigo-600/80' : 'text-slate-500'}`}>
                  {userData?.businessProfile?.businessName || "Your service provider account"}
                </p>
              </div>
              {!currentActiveRole && (
                <CheckCircleIcon className="text-indigo-500 w-6 h-6 animate-fade-in" />
              )}
            </div>
          </div>

          {sortedRoles.length > 0 && (
            <div className="h-px bg-slate-200 my-4" />
          )}

          {/* Role Items */}
          {sortedRoles.map((role: ServiceProviderRole, index: number) => {
            const isSelected = currentActiveRole?.serviceProviderReference === role.serviceProviderReference;


            return (
              <div
                key={index}
                role="button"
                tabIndex={0}
                onClick={() => handleRoleSelect(role)}
                onKeyDown={(e) => e.key === 'Enter' && handleRoleSelect(role)}
                aria-label={`Switch to ${role.businessName} as ${role.role}`}
                className={`
                  relative p-4 rounded-xl transition-all duration-200
                  ${isSelected
                    ? 'bg-gradient-to-r from-violet-50 to-indigo-50/70 border-2 border-indigo-400 shadow-sm'
                    : 'bg-white hover:bg-slate-50/50 border border-slate-200 cursor-pointer'
                  }
                  ${'hover:shadow-md hover:-translate-y-[1px]'}
                  focus:outline-none focus:ring-2 focus:ring-indigo-500/30
                `}
              >
                <div className="flex justify-between items-center">
                  <div className="space-y-1">
                    <p className={`text-lg font-semibold ${isSelected
                      ? 'text-indigo-700'
                      : 'text-slate-700'
                      }`}>
                      {role.businessName || 'Unnamed Business'}
                    </p>
                    <div className="space-y-0.5">
                      <p className={`text-sm ${isSelected
                        ? 'text-indigo-600/80'
                        : 'text-slate-500'
                        }`}>
                        {role.role} {role.name ? `- ${role.name}` : ''}

                      </p>
                    </div>
                  </div>
                  {isSelected && (
                    <CheckCircleIcon className="text-indigo-500 w-6 h-6 animate-fade-in" />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {(!sortedRoles || sortedRoles.length === 0) && (
          <div className="text-center py-8 px-6 bg-slate-50/50 rounded-xl border border-slate-200">
            <p className="text-slate-500">
              You don't have any additional roles assigned
            </p>
          </div>
        )}
      </DialogContent>

      <DialogActions sx={{
        p: 2,
        pt: 1,
        borderTop: '1px solid rgba(0, 0, 0, 0.12)'
      }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderColor: theme.palette.grey[300],
            color: theme.palette.text.primary,
            '&:hover': {
              borderColor: theme.palette.grey[400],
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleSwitchModal;